import React, { useState, useEffect, useCallback, useRef } from 'react';

import { toast } from 'react-toastify';

import {
    Box, Typography, TextField, CircularProgress,
    Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Paper,
    Chip, IconButton, Tooltip, Button,
    FormControl, InputLabel, Select, MenuItem,
} from '@material-ui/core';
import { AddCircleOutline, BlockRounded, LockOpenRounded, HistoryRounded } from '@material-ui/icons';

import Dialog from '../../commonModules/interface/Dialog';

// Même table que usersRoles.jsx (qui l'exporte aussi) : dupliquée ici plutôt qu'importée
// pour éviter une dépendance circulaire entre les deux modules (usersRoles.jsx importe
// UserAccounts) — option explicitement permise pour cette table.
const MANAGEABILITY_REASON_LABEL = {
    no_admin: "créateur non renseigné",
    owner_orphan: "créateur rattaché à aucun compte",
    owner_multi_account: "créateur rattaché à plusieurs comptes",
    owner_wrong_account: "créateur rattaché à un autre compte",
    admin_mismatch: "administrateur du compte incohérent",
}
const reasonLabel = (reason) => MANAGEABILITY_REASON_LABEL[reason] || reason || "";

// Rôle GLOBAL de l'utilisateur (domainModel User.Role), distinct du roleID par compte.
// 2 et 5 sont les seuls rôles à privilège cross-compte connus côté domainModel
// (cf. roleManagement.go:198 `userInfo.Role == 2 || userInfo.Role == 5`).
const GLOBAL_ROLE_LABEL = {
    2: "Admin",
    5: "SuperAdmin",
    6: "Custom admin",
}
const globalRoleLabel = (role) => `${GLOBAL_ROLE_LABEL[role] || "Rôle"} (${role})`

// Statut GLOBAL de l'utilisateur (domainModel StatusCode), distinct du status "active"/
// "suspended" par ligne de compte. 2 (validé) n'affiche aucun badge (team-fix #7, H-1).
const GLOBAL_STATUS_LABEL = {
    1: "En attente",
    3: "Réinit. mot de passe",
    4: "Bloqué",
}
const globalStatusLabel = (status) => GLOBAL_STATUS_LABEL[status] || ""

// Libellés des motifs d'erreur amont ({errorCode, content}), sur les mêmes routes que le
// chemin « Par compte » (roleManagement.go / userSuspension.go), plus les 3 motifs propres
// à la vue par utilisateur (target_is_admin_account, role_not_in_account,
// account_binding_mismatch, ambiguous_email, invalid_email).
const ERROR_LABEL = {
    target_is_admin_account: "Le créateur du compte ne peut pas être modifié ainsi",
    role_not_in_account: "Ce rôle n'appartient pas à ce compte",
    account_binding_mismatch: "Compte non exploitable : rattachement incohérent",
    ambiguous_email: "Plusieurs comptes correspondent à cet email, action impossible",
    invalid_email: "Adresse email invalide",
    cannot_suspend_self: "Vous ne pouvez pas suspendre votre propre accès",
    cannot_suspend_account_creator: "Le créateur du compte ne peut pas être suspendu",
    cannot_suspend_admin_role: "Un administrateur ne peut pas être suspendu",
    not_active: "Cet accès n'est pas actif",
    not_suspended: "Cet accès n'est pas suspendu",
    concurrent_modification: "Modification concurrente détectée, réessayez",
    // Motifs propres a la route de suspension globale (superadmin/user/:id/suspend-all).
    account_admin_missing: "Compte non exploitable : administrateur non renseigné",
    too_many_accounts: "Trop de comptes pour une suspension globale en une seule écriture",
    // Arbitrage team-lead (2026-09-08) : userName/userPassword peuvent être omis sur
    // addUser pour un email déjà existant ; ces deux motifs ne sortent que si l'email ne
    // correspond à aucun utilisateur (création implicite refusée depuis cette vue).
    password_required: "Utilisateur inexistant : créez-le d'abord depuis la vue par compte",
    username_required: "Utilisateur inexistant : créez-le d'abord depuis la vue par compte",
}
const errorLabel = (res) => {
    const code = res && res.content
    if (!code) { return "Un problème est survenu" }
    return ERROR_LABEL[code] || code
}
// libellés des actions d'historique (même table que UsersList.jsx ACCESS_ACTION_LABEL)
const ACCESS_ACTION_LABEL = { suspend: "Suspension", reactivate: "Réactivation" }
const accessActionLabel = (action) => ACCESS_ACTION_LABEL[action] || action || ""
// Portée de la ligne d'historique (AC-12). Le serveur pose scope:"global" sur les
// lignes écrites par une suspension « tous ses comptes ». Le champ est absent des
// lignes antérieures et de tout le chemin admin de compte : le libellé y reste
// exactement celui d'aujourd'hui, sans colonne supplémentaire (AC-18).
const accessEntryLabel = (entry) => ((entry && entry.scope === 'global')
    ? accessActionLabel(entry.action) + " (tous ses comptes)"
    : accessActionLabel(entry && entry.action))
const formatAccessDate = (value) => {
    if (!value) { return "" }
    const date = new Date(value)
    if (isNaN(date.getTime())) { return value }
    return date.toLocaleString('fr-FR')
}

function UserAccounts(props) {
    const [users, setUsers] = useState([])
    const [usersLoading, setUsersLoading] = useState(false)
    const [usersError, setUsersError] = useState("")
    const [search, setSearch] = useState("")

    const [selectedUserId, setSelectedUserId] = useState("")
    const [selectedUser, setSelectedUser] = useState(null) // {userID,email,name,role,status,accounts}
    const [accountsLoading, setAccountsLoading] = useState(false)
    const [accountsError, setAccountsError] = useState("")
    // Jeton de séquence : deux sélections rapprochées peuvent voir leurs réponses arriver
    // dans le désordre (même patron que usersRoles.jsx). On ignore toute réponse dont le
    // jeton n'est plus le courant, succès comme échec.
    const selectSeq = useRef(0)

    // roleList par compte, chargée à l'ouverture du Select de rôle et mise en cache.
    const [roleCache, setRoleCache] = useState({})
    const [roleLoading, setRoleLoading] = useState({})

    // action en cours (changement de rôle / suspension / réactivation) sur une ligne donnée :
    // désactive uniquement la ligne concernée pendant l'appel réseau. Indexé sur accessUserID
    // (repli sur accountID) : le backend peut rendre deux lignes pour un même compte (team-fix
    // #7, M-2), accountID seul ne serait pas unique dans ce cas.
    const [pendingRowKey, setPendingRowKey] = useState("")
    const rowKey = (account) => account.accessUserID || account.accountID

    const [confirmSuspendAccount, setConfirmSuspendAccount] = useState(null)
    // Dialogue de CHOIX « ce compte seulement / tous ses comptes », distinct du dialogue
    // simple ci-dessus : les deux ne s'ouvrent jamais ensemble et sont réinitialisés
    // ensemble par postAccountAction (AC-26).
    const [confirmSuspendChoice, setConfirmSuspendChoice] = useState(null)

    const [historyTarget, setHistoryTarget] = useState(null)
    const [historyList, setHistoryList] = useState([])
    const [historyLoading, setHistoryLoading] = useState(false)
    const [historyError, setHistoryError] = useState("")

    const [addOpen, setAddOpen] = useState(false)
    const [addAccountID, setAddAccountID] = useState("")
    const [addRoleID, setAddRoleID] = useState("")
    const [addRoleList, setAddRoleList] = useState([])
    const [addRoleLoading, setAddRoleLoading] = useState(false)
    const [addSubmitting, setAddSubmitting] = useState(false)

    const loadUsers = useCallback(async () => {
        setUsersLoading(true)
        setUsersError("")
        try {
            const req = await fetch('/superadmin/user/list', {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
            })
            const res = await req.json()
            if (req.ok && res && Array.isArray(res.userList)) {
                setUsers(res.userList)
            } else {
                setUsersError((res && res.content) ? errorLabel(res) : "Impossible de charger la liste des utilisateurs")
            }
        } catch (err) {
            console.log("[ERROR] superadmin user list", err);
            setUsersError("Erreur lors du chargement de la liste des utilisateurs")
        }
        setUsersLoading(false)
    }, [])

    useEffect(() => {
        loadUsers()
    }, [loadUsers])

    const loadUserAccounts = useCallback(async (userID) => {
        if (!userID) {
            selectSeq.current += 1
            setSelectedUserId("")
            setSelectedUser(null)
            setAccountsLoading(false)
            return
        }
        const seq = ++selectSeq.current
        setSelectedUserId(userID)
        setSelectedUser(null)
        setAccountsLoading(true)
        setAccountsError("")
        try {
            const req = await fetch('/superadmin/user/' + encodeURIComponent(userID) + '/accounts', {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
            })
            const res = await req.json()
            if (seq !== selectSeq.current) { return }
            if (req.ok && res && Array.isArray(res.accounts)) {
                // La réponse entière est conservée : outre {userID,email,name,role,status,
                // accounts}, elle porte l'en-tête globalSuspensionAvailable /
                // globalSuspensionCount / globalSuspensionReason, produit par la fonction de
                // décision serveur elle-même. Le front ne dérive rien (AC-11, PM-3).
                setSelectedUser(res)
            } else {
                setAccountsError((res && res.content) ? errorLabel(res) : "Impossible de charger les comptes de cet utilisateur")
            }
        } catch (err) {
            if (seq !== selectSeq.current) { return }
            console.log("[ERROR] superadmin user accounts", err);
            setAccountsError("Erreur lors du chargement des comptes")
        }
        if (seq === selectSeq.current) {
            setAccountsLoading(false)
        }
    }, [])

    const filteredUsers = search === "" ? users : users.filter((u) => {
        const s = search.toLowerCase()
        return (u.email || "").toLowerCase().includes(s) || (u.name || "").toLowerCase().includes(s)
    })

    const loadRolesForAccount = (accountID) => {
        if (roleCache[accountID] || roleLoading[accountID]) { return }
        setRoleLoading((prev) => ({ ...prev, [accountID]: true }))
        fetch('/superadmin/account/' + accountID + '/roleList', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
        })
            .then((r) => r.json().then((res) => ({ ok: r.ok, res })))
            .then(({ ok, res }) => {
                if (ok && res && Array.isArray(res.roleList)) {
                    setRoleCache((prev) => ({ ...prev, [accountID]: res.roleList }))
                } else {
                    toast.error(errorLabel(res))
                }
                setRoleLoading((prev) => ({ ...prev, [accountID]: false }))
            })
            .catch((err) => {
                console.log("[ERROR] superadmin account roleList", err);
                toast.error("Erreur lors du chargement des rôles")
                setRoleLoading((prev) => ({ ...prev, [accountID]: false }))
            })
    }

    // Appel commun updateUser / suspendUser / reactivateUser sur un compte du superadmin
    // par utilisateur : même patron que UsersList.sendAccessAction, adapté au fait que
    // l'utilisateur cible reste le même et que seul le compte change d'une ligne à l'autre.
    const postAccountAction = async (account, url, body, successMessage) => {
        setPendingRowKey(rowKey(account))
        try {
            const req = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
                credentials: 'same-origin',
            })
            const res = await req.json()
            console.log("[INFO] superadmin user account action", { url, status: req.status, res });

            if (req.status === 409 && res && res.content === 'concurrent_modification') {
                // Écriture concurrente : rien n'est cassé, on recharge et on invite à rejouer.
                toast.warn("Modification concurrente détectée, réessayez")
                await loadUserAccounts(selectedUser.userID)
            } else if (req.status === 409) {
                // Refus permanent (account_binding_mismatch...) : rejouer ne changera rien.
                toast.error(errorLabel(res))
            } else if (req.ok && res && !res.errorCode) {
                // D9 : l'action reste effective même si l'historique n'a pas pu être écrit
                // côté domainModel. On le signale sans transformer l'action en échec, même
                // patron que UsersList.sendAccessAction.
                if (res.changed === true && res.historyRecorded === false) {
                    toast.warn("Action appliquée, mais l'historique n'a pas pu être enregistré")
                }
                // successMessage peut être une fonction de la réponse : la suspension globale
                // affiche les compteurs rendus par le serveur (accountsSuspended,
                // accountsAlreadySuspended) plutôt qu'un N recalculé côté front (R-9).
                toast.success(typeof successMessage === 'function' ? successMessage(res) : successMessage)
                await loadUserAccounts(selectedUser.userID)
            } else {
                toast.error(errorLabel(res))
            }
        } catch (err) {
            console.log("[ERROR] superadmin user account action", err);
            toast.error("Erreur lors de la mise à jour")
        }
        setPendingRowKey("")
        setConfirmSuspendAccount(null)
        // AC-26 : les DEUX dialogues sont refermés, en succès comme en échec. Sans cette
        // ligne, le dialogue de choix resterait ouvert après l'action.
        setConfirmSuspendChoice(null)
    }

    const handleChangeRole = (account, roleID) => {
        if (!selectedUser) { return }
        postAccountAction(
            account,
            '/superadmin/account/' + account.accountID + '/updateUser',
            { email: selectedUser.email, roleID },
            "Rôle mis à jour",
        )
    }
    const handleSuspend = (account) => {
        postAccountAction(
            account,
            '/superadmin/account/' + account.accountID + '/suspendUser',
            { userID: account.accessUserID },
            "Accès suspendu",
        )
    }
    // Suspension « tous ses comptes » : route scopée UTILISATEUR, le compte ne figure pas
    // dans l'URL. postAccountAction est réutilisée telle quelle pour partager le traitement
    // des 409, le rafraîchissement et les toasts. Le corps est vide : la route lit sa cible
    // dans le chemin. La ligne passée reste celle d'où part l'action, pour que l'indicateur
    // de chargement porte sur elle.
    const handleSuspendAll = (account) => {
        if (!selectedUser) { return }
        postAccountAction(
            account,
            '/superadmin/user/' + selectedUser.userID + '/suspendAll',
            {},
            (res) => {
                const suspended = (res && res.accountsSuspended) || 0
                const already = (res && res.accountsAlreadySuspended) || 0
                return already > 0
                    ? `Accès suspendu sur ${suspended} compte(s), ${already} déjà suspendu(s)`
                    : `Accès suspendu sur ${suspended} compte(s)`
            },
        )
    }
    const handleReactivate = (account) => {
        postAccountAction(
            account,
            '/superadmin/account/' + account.accountID + '/reactivateUser',
            { userID: account.accessUserID },
            "Accès rétabli",
        )
    }

    const openHistory = async (account) => {
        setHistoryTarget(account)
        setHistoryList([])
        setHistoryLoading(true)
        setHistoryError("")
        try {
            const req = await fetch(
                '/superadmin/account/' + account.accountID + '/userHistory?userID=' + encodeURIComponent(account.accessUserID || ""),
                {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'same-origin',
                },
            )
            const res = await req.json()
            if (req.ok && res && !res.errorCode) {
                setHistoryList(Array.isArray(res.history) ? res.history : [])
                setHistoryLoading(false)
            } else {
                setHistoryLoading(false)
                setHistoryError(errorLabel(res))
            }
        } catch (err) {
            console.log("[ERROR] superadmin user history", err);
            setHistoryLoading(false)
            setHistoryError("Erreur lors du chargement de l'historique")
        }
    }
    const closeHistory = () => {
        setHistoryTarget(null)
        setHistoryList([])
        setHistoryLoading(false)
        setHistoryError("")
    }

    // Email du SuperAdmin connecté, pour ne jamais lui proposer de retirer ses propres droits
    // (AC-U6). Aucune source fiable de cet email n'existe aujourd'hui dans le store (authReducer
    // ne porte pas le champ email) : on tente les emplacements plausibles ; si absent, la règle
    // « vous-même » retombe simplement inactive (un seul console.warn, team-fix #7 L-2).
    const currentSuperAdminEmail = (props.auth && props.auth.user && props.auth.user.email) || props.superAdminEmail || ""
    const warnedNoEmailRef = useRef(false)
    useEffect(() => {
        if (!currentSuperAdminEmail && !warnedNoEmailRef.current) {
            console.warn("[WARN] superadmin userAccounts : email du SuperAdmin connecté introuvable dans le store, la règle « vous-même » est désactivée")
            warnedNoEmailRef.current = true
        }
    }, [currentSuperAdminEmail])
    const isSelfSelected = !!currentSuperAdminEmail && !!selectedUser && selectedUser.email === currentSuperAdminEmail
    const isGlobalSuperAdmin = !!selectedUser && (selectedUser.role === 2 || selectedUser.role === 5)

    // Motif de blocage d'une ligne compte : accessActionsAllowed d'abord (le serveur dit
    // que les actions d'accès seraient refusées sur ce compte), puis créateur du compte,
    // puis rôle global 2/5 de l'utilisateur sélectionné, puis "vous-même" — même hiérarchie
    // que UsersList.jsx.
    // accessActionsAllowed est un champ serveur additif : absent d'une réponse ancienne, il
    // se relit en undefined et BLOQUE. Pour une affordance, refuser par omission est le bon
    // sens de l'erreur (plan axe C, option C2).
    const rowBlock = (account) => {
        if (account.accessActionsAllowed !== true) {
            // Le libellé ne doit JAMAIS être vide, sinon le motif remonté est "" et
            // l'appelant en déduit « pas de blocage » : la ligne redeviendrait actionnable
            // sur un compte sain servi par un serveur antérieur à ce champ, ce qui annule
            // le refus par omission voulu ici.
            return reasonLabel(account.reason) || "Compte non exploitable"
        }
        if (account.isAccountAdmin === true) {
            return "Créateur du compte"
        }
        if (isGlobalSuperAdmin) {
            return "Super administrateur"
        }
        if (isSelfSelected) {
            return "Vous-même"
        }
        return ""
    }

    // Avertissement SANS blocage (AC-17). Le compte n'est pas exploitable pour les mutations
    // (manageable=false) mais le serveur tolère suspendre, réactiver et l'historique, au seul
    // motif admin_mismatch. Le libellé reste affiché ; seuls le sélecteur de rôle et l'ajout
    // d'accès restent désactivés.
    const rowWarning = (account) =>
        (account.manageable === false && account.accessActionsAllowed === true) ? reasonLabel(account.reason) : ""

    // Comptes manageable non encore rattachés à l'utilisateur sélectionné, pour le Select
    // du dialogue d'ajout d'accès. Le filtre reste sur manageable : l'ajout d'accès est une
    // mutation, la tolérance admin_mismatch ne l'assouplit pas.
    const alreadyLinkedIDs = new Set(((selectedUser && selectedUser.accounts) || []).map((a) => a.accountID))
    const availableAccounts = (props.accounts || []).filter((acc) => acc.manageable !== false && !alreadyLinkedIDs.has(acc.accountID))

    const openAddDialog = () => {
        setAddOpen(true)
        setAddAccountID("")
        setAddRoleID("")
        setAddRoleList([])
    }
    const closeAddDialog = () => {
        setAddOpen(false)
        setAddAccountID("")
        setAddRoleID("")
        setAddRoleList([])
    }
    const handleAddAccountChange = (accountID) => {
        setAddAccountID(accountID)
        setAddRoleID("")
        setAddRoleList([])
        if (!accountID) { return }
        setAddRoleLoading(true)
        fetch('/superadmin/account/' + accountID + '/roleList', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
        })
            .then((r) => r.json().then((res) => ({ ok: r.ok, res })))
            .then(({ ok, res }) => {
                if (ok && res && Array.isArray(res.roleList)) {
                    setAddRoleList(res.roleList)
                } else {
                    toast.error(errorLabel(res))
                }
                setAddRoleLoading(false)
            })
            .catch((err) => {
                console.log("[ERROR] superadmin add access roleList", err);
                toast.error("Erreur lors du chargement des rôles")
                setAddRoleLoading(false)
            })
    }
    const handleAddAccess = async () => {
        if (!selectedUser || !addAccountID || !addRoleID) { return }
        setAddSubmitting(true)
        try {
            const req = await fetch('/superadmin/account/' + addAccountID + '/addUser', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    // userName / userPassword omis : arbitrage team-lead (2026-09-08), le
                    // backend les rend facultatifs pour un email déjà existant (ils ne servent
                    // qu'à la création implicite d'un utilisateur, refusée depuis cette vue :
                    // password_required / username_required si l'email ne correspond à rien).
                    email: selectedUser.email,
                    roleID: addRoleID,
                }),
                credentials: 'same-origin',
            })
            const res = await req.json()
            if (req.ok && res && !res.errorCode) {
                toast.success("Accès ajouté")
                closeAddDialog()
                await loadUserAccounts(selectedUser.userID)
            } else {
                toast.error(errorLabel(res))
            }
        } catch (err) {
            console.log("[ERROR] superadmin add access", err);
            toast.error("Erreur lors de l'ajout de l'accès")
        }
        setAddSubmitting(false)
    }

    return (
        <Box>
            <Box mb={2} maxWidth={480}>
                <TextField
                    fullWidth
                    label="Rechercher un utilisateur"
                    placeholder="Email ou nom"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </Box>

            {usersLoading &&
                <Box mb={1}><CircularProgress size={20} /></Box>
            }
            {!usersLoading && usersError !== "" &&
                <Typography color="error" variant="body2">{usersError}</Typography>
            }

            {!usersLoading && usersError === "" &&
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Email</TableCell>
                                <TableCell>Nom</TableCell>
                                <TableCell>Rôle global</TableCell>
                                <TableCell>Comptes</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredUsers.map((u) => (
                                <TableRow
                                    key={u.userID}
                                    hover
                                    selected={u.userID === selectedUserId}
                                    onClick={() => loadUserAccounts(u.userID)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <TableCell>
                                        {u.email}
                                        {globalStatusLabel(u.globalStatus) !== "" &&
                                            <Chip label={globalStatusLabel(u.globalStatus)} size="small" style={{ marginLeft: 8 }} />
                                        }
                                    </TableCell>
                                    <TableCell>{u.name}</TableCell>
                                    <TableCell>{globalRoleLabel(u.role)}</TableCell>
                                    <TableCell>{u.accountCount}</TableCell>
                                </TableRow>
                            ))}
                            {filteredUsers.length === 0 &&
                                <TableRow>
                                    <TableCell colSpan={4}>Aucun utilisateur ne correspond à cette recherche</TableCell>
                                </TableRow>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            }

            {accountsLoading &&
                <Box mt={2}><CircularProgress size={20} /></Box>
            }
            {!accountsLoading && accountsError !== "" &&
                <Typography color="error" variant="body2" style={{ marginTop: 16 }}>{accountsError}</Typography>
            }

            {selectedUser && !accountsLoading && accountsError === "" &&
                <Box mt={3}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6">
                            Comptes de {selectedUser.name || selectedUser.email} ({selectedUser.email})
                        </Typography>
                        {isGlobalSuperAdmin ?
                            // M-1 : le serveur refuse target_is_admin_account pour un rôle global
                            // 2/5, on désactive le bouton en amont plutôt que de laisser échouer
                            // la requête.
                            <Tooltip title="Un administrateur global (rôle 2 ou 5) ne peut pas être ajouté à un compte depuis cette vue">
                                <span>
                                    <Button variant="contained" color="primary" startIcon={<AddCircleOutline />} disabled>
                                        Ajouter un Accès à un Compte
                                    </Button>
                                </span>
                            </Tooltip>
                        :
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddCircleOutline />}
                                onClick={openAddDialog}
                                disabled={availableAccounts.length === 0}
                            >
                                Ajouter un Accès à un Compte
                            </Button>
                        }
                    </Box>
                    {!isGlobalSuperAdmin && availableAccounts.length === 0 && !props.accountsLoading &&
                        <Typography variant="body2" color="textSecondary">
                            Aucun compte exploitable disponible pour un nouvel accès.
                        </Typography>
                    }

                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Compte</TableCell>
                                    <TableCell>Pays</TableCell>
                                    <TableCell>Rôle</TableCell>
                                    <TableCell>Statut</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedUser.accounts.map((account) => {
                                    const suspended = account.status === 'suspended'
                                    const blockReason = rowBlock(account)
                                    const disabled = blockReason !== ""
                                    // Avertissement sans blocage : les actions d'accès restent
                                    // actives, le rôle reste verrouillé (AC-17).
                                    const warnReason = rowWarning(account)
                                    const rowPending = pendingRowKey === rowKey(account)
                                    // L-3 : l'option courante (roleID/roleName) est toujours présente dans
                                    // la liste, même après chargement de roleCache, pour ne jamais avoir
                                    // une Select value hors des options rendues (avertissement MUI).
                                    const cachedRoles = roleCache[account.accountID]
                                    const roleOptions = cachedRoles
                                        ? (cachedRoles.some((r) => r.id === account.roleID) ? cachedRoles : [...cachedRoles, { id: account.roleID, name: account.roleName }])
                                        : [{ id: account.roleID, name: account.roleName }]
                                    return (
                                        <TableRow key={rowKey(account)} style={{ opacity: suspended ? 0.5 : 1 }}>
                                            <TableCell>{account.accountName}</TableCell>
                                            <TableCell>{account.country}</TableCell>
                                            <TableCell>
                                                <FormControl style={{ minWidth: 180 }} disabled={disabled || warnReason !== "" || rowPending}>
                                                    <InputLabel id={'role-select-' + account.accountID}>Rôle</InputLabel>
                                                    <Select
                                                        labelId={'role-select-' + account.accountID}
                                                        value={account.roleID}
                                                        onOpen={() => loadRolesForAccount(account.accountID)}
                                                        onChange={(e) => handleChangeRole(account, e.target.value)}
                                                    >
                                                        {roleOptions.map((r) => (
                                                            <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                {disabled &&
                                                    <Tooltip title={blockReason}>
                                                        <span style={{ marginLeft: 8, fontSize: '0.75rem', color: 'rgba(0,0,0,0.54)' }}>({blockReason})</span>
                                                    </Tooltip>
                                                }
                                                {!disabled && warnReason !== "" &&
                                                    <Tooltip title={warnReason + " — les actions d'accès restent possibles, pas le changement de rôle"}>
                                                        <span style={{ marginLeft: 8, fontSize: '0.75rem', color: '#b26a00' }}>({warnReason})</span>
                                                    </Tooltip>
                                                }
                                            </TableCell>
                                            <TableCell>
                                                {suspended && <Chip label="Suspendu" size="small" />}
                                            </TableCell>
                                            <TableCell>
                                                {suspended ?
                                                    <IconButton
                                                        className="btn btn-success btn-icon"
                                                        title="Rétablir l'accès"
                                                        disabled={rowPending}
                                                        onClick={() => handleReactivate(account)}
                                                    >
                                                        <LockOpenRounded />
                                                    </IconButton>
                                                : disabled ?
                                                    <Tooltip title={blockReason}>
                                                        <span>
                                                            <IconButton className="btn btn-danger btn-icon" disabled>
                                                                <BlockRounded />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                :
                                                    <IconButton
                                                        className="btn btn-danger btn-icon"
                                                        title="Suspendre l'accès"
                                                        disabled={rowPending}
                                                        // Dialogue de CHOIX seulement si le serveur
                                                        // dit que la ligne est le compte natif, que
                                                        // l'action globale aboutirait ET qu'elle
                                                        // porterait sur au moins deux comptes : sous
                                                        // ce seuil, le choix n'a pas d'objet et le
                                                        // dialogue simple suffit (AC-8, AC-10).
                                                        // Le compteur vient du serveur. Aucun calcul
                                                        // local (AC-11, PM-3).
                                                        onClick={() => (account.isNativeAccount === true && selectedUser.globalSuspensionAvailable === true && selectedUser.globalSuspensionCount >= 2)
                                                            ? setConfirmSuspendChoice(account)
                                                            : setConfirmSuspendAccount(account)}
                                                    >
                                                        <BlockRounded />
                                                    </IconButton>
                                                }
                                                <IconButton
                                                    className="btn btn-info btn-icon"
                                                    title="Historique des accès"
                                                    onClick={() => openHistory(account)}
                                                >
                                                    <HistoryRounded />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                                {selectedUser.accounts.length === 0 &&
                                    <TableRow>
                                        <TableCell colSpan={5}>Cet utilisateur n'a accès à aucun compte</TableCell>
                                    </TableRow>
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            }

            {confirmSuspendAccount &&
                <Dialog
                    title="Suspendre l'accès"
                    block={true}
                    close={() => setConfirmSuspendAccount(null)}
                    submitAction={() => handleSuspend(confirmSuspendAccount)}
                    submitTitle="Suspendre"
                    disabledSubmit={pendingRowKey === rowKey(confirmSuspendAccount)}
                >
                    <p>
                        Suspendre l'accès de <strong>{selectedUser && (selectedUser.name || selectedUser.email)}</strong> au compte <strong>{confirmSuspendAccount.accountName}</strong> ?
                    </p>
                    <p>
                        Son rôle est conservé et l'accès peut être rétabli à tout moment.
                    </p>
                </Dialog>
            }

            {confirmSuspendChoice && selectedUser &&
                // Dialogue à deux boutons via la prop `submit` du composant partagé, qui rend
                // le nœud fourni dans un DialogActions à côté d'« Annuler ». `submitAction`
                // n'est PAS passé : les deux blocs sont conditionnés séparément dans Dialog.jsx
                // et seraient rendus tous les deux (R-8).
                <Dialog
                    title="Suspendre l'accès"
                    block={true}
                    close={() => setConfirmSuspendChoice(null)}
                    submit={
                        <>
                            <Button
                                variant="contained"
                                color="secondary"
                                disabled={pendingRowKey === rowKey(confirmSuspendChoice)}
                                onClick={() => handleSuspend(confirmSuspendChoice)}
                            >
                                Ce compte seulement
                            </Button>
                            <Button
                                variant="contained"
                                color="secondary"
                                disabled={pendingRowKey === rowKey(confirmSuspendChoice)}
                                onClick={() => handleSuspendAll(confirmSuspendChoice)}
                            >
                                {`Tous ses comptes (${selectedUser.globalSuspensionCount})`}
                            </Button>
                        </>
                    }
                >
                    <p>
                        Suspendre l'accès de <strong>{selectedUser.name || selectedUser.email}</strong> ?
                    </p>
                    <p>
                        <strong>Ce compte seulement</strong> : seul l'accès au compte <strong>{confirmSuspendChoice.accountName}</strong> est suspendu.
                    </p>
                    <p>
                        <strong>Tous ses comptes ({selectedUser.globalSuspensionCount})</strong> : l'accès est suspendu sur l'ensemble de ses comptes en une seule action. Ses rôles sont conservés et chaque accès peut être rétabli compte par compte.
                    </p>
                </Dialog>
            }

            {historyTarget &&
                <Dialog
                    title="Historique des accès"
                    block={true}
                    maxWidth="md"
                    close={closeHistory}
                >
                    <p>
                        <strong>{selectedUser && (selectedUser.name || selectedUser.email)}</strong> — {historyTarget.accountName}
                    </p>
                    {historyLoading &&
                        <p>Chargement de l'historique...</p>
                    }
                    {!historyLoading && historyError !== "" &&
                        <p className="text-danger">{historyError}</p>
                    }
                    {!historyLoading && historyError === "" && historyList.length === 0 &&
                        <p>Aucune action enregistrée pour cet utilisateur sur ce compte.</p>
                    }
                    {!historyLoading && historyError === "" && historyList.length > 0 &&
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Action</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Auteur</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {historyList.map((entry, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{accessEntryLabel(entry)}</TableCell>
                                        <TableCell>{formatAccessDate(entry.date)}</TableCell>
                                        <TableCell>{entry.adminEmail || entry.adminID}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    }
                </Dialog>
            }

            {addOpen &&
                <Dialog
                    title="Ajouter un Accès à un Compte"
                    block={true}
                    close={closeAddDialog}
                    submitAction={handleAddAccess}
                    submitTitle="Ajouter"
                    disabledSubmit={addSubmitting || !addAccountID || !addRoleID}
                >
                    <p>
                        Rattacher <strong>{selectedUser && (selectedUser.name || selectedUser.email)}</strong> à un nouveau compte.
                    </p>
                    <Box mb={2}>
                        <FormControl fullWidth>
                            <InputLabel id="add-access-account-label">Compte</InputLabel>
                            <Select
                                labelId="add-access-account-label"
                                value={addAccountID}
                                onChange={(e) => handleAddAccountChange(e.target.value)}
                            >
                                {availableAccounts.map((acc) => (
                                    <MenuItem key={acc.accountID} value={acc.accountID}>
                                        {`${acc.name} — ${acc.country} — ${acc.adminEmail}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box mb={1}>
                        <FormControl fullWidth disabled={!addAccountID || addRoleLoading}>
                            <InputLabel id="add-access-role-label">Rôle</InputLabel>
                            <Select
                                labelId="add-access-role-label"
                                value={addRoleID}
                                onChange={(e) => setAddRoleID(e.target.value)}
                            >
                                {addRoleList.map((r) => (
                                    <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {addRoleLoading &&
                            <Box mt={1}><CircularProgress size={16} /></Box>
                        }
                    </Box>
                </Dialog>
            }
        </Box>
    );
}

export default UserAccounts;
